/**
 * Checkout experiences available for Mercado Pago.
 *
 * 'brick'  -> Card Payment Brick: Mercado Pago renders the whole form.
 * 'fields' -> Secure Fields: our own layout, card inputs are Mercado Pago iframes.
 */
const MERCADOPAGO_EXPERIENCES = ['brick', 'fields']
const MERCADOPAGO_DEFAULT_EXPERIENCE = 'brick'

/**
 * Resolve which Mercado Pago checkout experience to render.
 *
 * Stage 1: `provider` is always empty, so the query param decides and otherwise
 * the default wins. Stage 2 will pass the value configured on the account's
 * Provider and this function stays as is; the query param survives as a QA and
 * support override. An unknown value falls back to the default, never throws.
 */
const resolveMercadopagoExperience = ({ query = '', provider = '' } = {}) => {
  if (MERCADOPAGO_EXPERIENCES.includes(query)) {
    return query
  }
  if (MERCADOPAGO_EXPERIENCES.includes(provider)) {
    return provider
  }
  return MERCADOPAGO_DEFAULT_EXPERIENCE
}

/**
 * Mercado Pago rejects createCardToken with an array of { code, description },
 * where the description is an English sentence naming the offending field
 * ("invalid parameter identificationNumber"). Matching on the field name
 * survives both rewording and changes to the numeric codes.
 */
const CARD_TOKEN_FIELD_ERRORS = {
  cardNumber: 'El número de la tarjeta no es válido.',
  cardholderName: 'El nombre del titular no es válido.',
  expirationDate: 'La fecha de vencimiento no es válida.',
  expirationMonth: 'La fecha de vencimiento no es válida.',
  expirationYear: 'La fecha de vencimiento no es válida.',
  identificationNumber: 'El RUT del pagador no es válido.',
  identificationType: 'El tipo de documento no es válido.',
  securityCode: 'El código de seguridad no es válido.'
}

const resolveCardTokenError = (error) => {
  const items = Array.isArray(error) ? error : [error]
  const descriptions = items
    .map(item => (item && (item.description || item.message)) || '')
    .join(' ')
  const field = Object.keys(CARD_TOKEN_FIELD_ERRORS).find(name => descriptions.includes(name))
  return field ? CARD_TOKEN_FIELD_ERRORS[field] : 'Revisa los datos de tu tarjeta.'
}

/**
 * Mercado Pago wants the RUT as digits plus the verifier, with no separators:
 * 18.040.901-7 -> 180409017. Sending it with the hyphen comes back as
 * "invalid parameter identificationNumber". The verifier can be a K.
 */
const toMercadopagoRut = (rut) => (rut || '').replace(/[^0-9kK]/g, '').toUpperCase()

export {
  MERCADOPAGO_DEFAULT_EXPERIENCE,
  MERCADOPAGO_EXPERIENCES,
  resolveCardTokenError,
  resolveMercadopagoExperience,
  toMercadopagoRut
}
